import React, { useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import InputAdornment from '@mui/material/InputAdornment';
import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import SearchIcon from '@mui/icons-material/Search';

interface ProductTableToolbarProps {
  productoCount: number;
  soloLectura?: boolean;
  busqueda: string;
  onBusquedaChange: (busqueda: string) => void;
  // SPEC-165
  lineasCount: number;
  onAplicarUtilidadMasiva: (utilidad: number) => void;
  // SPEC-181
  onAplicarCantidadMasiva: (cantidad: number) => void;
}

// SPEC-165 (RN-005): % válido entero/decimal dentro de [0, 99]. Vacío o fuera de rango ⇒ inválido.
function parseUtilidad(input: string): number | null {
  const trimmed = input.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0 || value > 99) return null;
  return value;
}

// SPEC-181 (RN-005): entero ≥ 1 (mismo criterio que el campo de cantidad de ProductRowCard).
// Vacío, no entero, cero o negativo ⇒ inválido.
function parseCantidadMasiva(input: string): number | null {
  const trimmed = input.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  if (value < 1) return null;
  return value;
}

export function ProductTableToolbar({
  productoCount,
  soloLectura = false,
  busqueda,
  onBusquedaChange,
  lineasCount,
  onAplicarUtilidadMasiva,
  onAplicarCantidadMasiva,
}: ProductTableToolbarProps) {
  const [ordenar, setOrdenar] = useState('nombre');
  // SPEC-165: el switch gobierna si el bloque aplica (RN-004) y nace en OFF (no persistido).
  const [aplicarActivo, setAplicarActivo] = useState(false);
  const [utilInput, setUtilInput] = useState('');
  // SPEC-181: control análogo para cantidad masiva, independiente del de utilidad.
  const [aplicarCantidadActivo, setAplicarCantidadActivo] = useState(false);
  const [cantidadInput, setCantidadInput] = useState('');

  const utilidad = parseUtilidad(utilInput);
  const controlBloqueado = soloLectura || lineasCount === 0; // RN-007 / UC-006
  const switchDisabled = controlBloqueado;
  const campoDisabled = controlBloqueado || !aplicarActivo; // RN-004
  const aplicarDisabled = campoDisabled || utilidad === null; // RN-005

  const handleAplicar = () => {
    if (aplicarDisabled || utilidad === null) return;
    onAplicarUtilidadMasiva(utilidad);
  };

  // SPEC-181 (RN-004/RN-005/RN-007/UC-008)
  const cantidadMasiva = parseCantidadMasiva(cantidadInput);
  const campoCantidadDisabled = controlBloqueado || !aplicarCantidadActivo;
  const aplicarCantidadDisabled = campoCantidadDisabled || cantidadMasiva === null;

  const handleAplicarCantidad = () => {
    if (aplicarCantidadDisabled || cantidadMasiva === null) return;
    onAplicarCantidadMasiva(cantidadMasiva);
  };

  return (
    <Box
      data-testid="product-table-toolbar"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: '#FFFFFF',
        border: '1px solid #E6E6EB',
        borderRadius: '12px',
        minHeight: 56,
        px: 2,
        py: 1,
        mb: 1.5,
        gap: 2,
        flexWrap: 'wrap',
        boxShadow: '0 2px 8px rgba(30,30,60,0.05)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <TextField
          size="small"
          placeholder="Buscar producto..."
          value={busqueda}
          onChange={(e) => onBusquedaChange(e.target.value)}
          data-testid="input-buscar-producto"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#8A8F99', fontSize: 22 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: 240,
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              fontSize: 17,
              '& fieldset': { borderColor: '#E6E6EB' },
            },
          }}
        />
        <Typography sx={{ fontSize: 16, color: '#8A8F99', whiteSpace: 'nowrap' }}>
          {productoCount} producto{productoCount !== 1 ? 's' : ''}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        {/* SPEC-165: control gráfico de utilidad masiva */}
        <Box
          data-testid="utilidad-masiva-control"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 0.5,
            borderRadius: '8px',
            background: '#F7F7FA',
            border: '1px solid #E6E6EB',
          }}
        >
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={aplicarActivo}
                disabled={switchDisabled}
                onChange={(e) => setAplicarActivo(e.target.checked)}
                inputProps={{ 'aria-label': 'Aplicar utilidad a todos' } as React.InputHTMLAttributes<HTMLInputElement>}
                data-testid="switch-utilidad-masiva"
              />
            }
            label={<Typography sx={{ fontSize: 15, color: '#4A4A55' }}>Aplicar a todos</Typography>}
            sx={{ mr: 0.5 }}
          />
          <TextField
            size="small"
            type="number"
            placeholder="%"
            value={utilInput}
            disabled={campoDisabled}
            onChange={(e) => setUtilInput(e.target.value)}
            data-testid="input-utilidad-masiva"
            inputProps={{ min: 0, max: 99, 'aria-label': 'Porcentaje de utilidad' }}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            sx={{
              width: 96,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                fontSize: 16,
                background: '#FFFFFF',
                '& fieldset': { borderColor: '#E6E6EB' },
              },
            }}
          />
          <Button
            variant="contained"
            size="small"
            disableElevation
            disabled={aplicarDisabled}
            onClick={handleAplicar}
            data-testid="btn-aplicar-utilidad-masiva"
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: 15,
              fontWeight: 600,
            }}
          >
            Aplicar
          </Button>
        </Box>

        {/* SPEC-181: control gráfico de cantidad masiva */}
        <Box
          data-testid="cantidad-masiva-control"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 0.5,
            borderRadius: '8px',
            background: '#F7F7FA',
            border: '1px solid #E6E6EB',
          }}
        >
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={aplicarCantidadActivo}
                disabled={switchDisabled}
                onChange={(e) => setAplicarCantidadActivo(e.target.checked)}
                inputProps={{ 'aria-label': 'Aplicar cantidad a todos' } as React.InputHTMLAttributes<HTMLInputElement>}
                data-testid="switch-cantidad-masiva"
              />
            }
            label={<Typography sx={{ fontSize: 15, color: '#4A4A55' }}>Aplicar a todos</Typography>}
            sx={{ mr: 0.5 }}
          />
          <TextField
            size="small"
            type="number"
            placeholder="Cant."
            value={cantidadInput}
            disabled={campoCantidadDisabled}
            onChange={(e) => setCantidadInput(e.target.value)}
            data-testid="input-cantidad-masiva"
            inputProps={{ min: 1, inputMode: 'numeric', 'aria-label': 'Cantidad' }}
            sx={{
              width: 96,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                fontSize: 16,
                background: '#FFFFFF',
                '& fieldset': { borderColor: '#E6E6EB' },
              },
            }}
          />
          <Button
            variant="contained"
            size="small"
            disableElevation
            disabled={aplicarCantidadDisabled}
            onClick={handleAplicarCantidad}
            data-testid="btn-aplicar-cantidad-masiva"
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: 15,
              fontWeight: 600,
            }}
          >
            Aplicar
          </Button>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 16, color: '#8A8F99' }}>Ordenar por</Typography>
          <Select
            size="small"
            value={ordenar}
            onChange={(e) => setOrdenar(e.target.value)}
            sx={{
              fontSize: 16,
              borderRadius: '8px',
              minWidth: 150,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E6E6EB' },
            }}
          >
            <MenuItem value="nombre">Nombre</MenuItem>
            <MenuItem value="proveedor">Proveedor</MenuItem>
            <MenuItem value="cantidad">Cantidad</MenuItem>
            <MenuItem value="precioVenta">Precio de venta</MenuItem>
          </Select>
        </Box>
      </Box>
    </Box>
  );
}
